import React, { useEffect, useState, useContext } from "react";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import { useStyles } from "Styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMsg } from "../../hooks/MsgProvider";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import useModal from "hooks/useModal";
import ConfirmDialog from "components/common/ConfirmDialog";
import { isMobilePhone } from "components/RenderPcOrMobile";
import { UserContext } from "hooks/UserContext";
import NewPWInput from "components/common/NewPWInput";
import usePwdValidation from "hooks/usePwdValidation";
import { message, confirmDialog } from "components/common/Constants";
import { changePwd, resetPwdFirstLogin, logout } from "api";
import { useLocation, useHistory } from "react-router-dom";
import LockPromptDialog from "components/common/LockPromptDialog";
import LoadingScreen from "components/common/LoadingScreen";
import { rsaOaepEncrypt } from "utils/encrypt";

const schema = yup.object().shape({
	currentPwd: yup.string().trim().required("Current Password is a required field."),
	newPwd: yup.string().trim().required("New Password is a required field."),
	confirmPwd: yup
		.string()
		.trim()
		.oneOf([yup.ref("newPwd"), null], message.PasswordMustBeTheSame)
		.required("Confirm Password is a required field."),
});

const defaultValues = {
	currentPwd: "",
	newPwd: "",
	confirmPwd: "",
};

export default function ChangePwd() {
	const classes = useStyles();
	const location = useLocation();
	const history = useHistory();
	const [showLockTip, setShowLockTip] = useState(false);
	const [lockTipContent, setLockTipContent] = useState("");
	const { setSuccessMsg, setErrMsg } = useMsg();
	const showModal = useModal();
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const changePwdData = sessionStorage.getItem("changePwdData")
		? JSON.parse(sessionStorage.getItem("changePwdData"))
		: {};

	const {
		register,
		handleSubmit,
		control,
		reset,
		watch,
		formState: { errors },
		clearErrors,
	} = useForm({
		mode: "onChange",
		resolver: yupResolver(schema),
		defaultValues: defaultValues,
	});
	const watchConfirmPassword = watch("confirmPwd");
	const watchNewPassword = watch("newPwd");

	useEffect(() => {
		const token = sessionStorage.getItem("accessToken");
		if (!token) {
			history.replace("/login");
		}
	}, []);

	useEffect(() => {
		if (watchNewPassword === watchConfirmPassword) {
			clearErrors("confirmPwd");
		}
	}, [watchNewPassword]);

	const { user } = useContext(UserContext);
	const getUserEmail = () => {
		if (location.pathname === "/change-pwd-first-login") {
			return changePwdData?.email;
		}
		return user?.user?.email;
	};
	const email = getUserEmail();

	const { atLeast8Valid, atLeast1Num, atLeast1LowAndUp, atLeast1Special, notContainEmail, saveEnabled } =
		usePwdValidation(watchNewPassword, email);

	const commonReset = async (data) => {
		const res = await changePwd({
			...data,
			userId: user?.user?.id,
		});
		setSuccessMsg(res.data);
		reset(defaultValues);
	};

	const firstLoginReset = async (data) => {
		try {
			const res = await resetPwdFirstLogin({
				...data,
				email,
			});
			if (res.data) {
				setLockTipContent("Your password has been updated successfully. Please keep your new password safe.");
				setShowLockTip(true);

				await logout({
					accToken: sessionStorage.getItem("accessToken"),
					refreshToken: sessionStorage.getItem("refreshToken"),
				});
				sessionStorage.clear();
			}
		} catch (error) {
			console.log("error", error);
			if (error?.response?.status === 401) {
				setErrMsg(error?.response?.data?.message || "Reset password fail.");
				setTimeout(() => {
					history.replace("/login");
					sessionStorage.clear();
				}, 6000);
			}
		}
	};

	const onSubmit = (data) => {
		showModal((pros) => (
			<ConfirmDialog
				{...pros}
				description={confirmDialog.ChangePassword}
				onConfirm={async () => {
					setIsLoading(true);
					const currentPassword = await rsaOaepEncrypt(data.currentPwd);
					const plainPassword = await rsaOaepEncrypt(data.newPwd);
					const confirmPlainPassword = await rsaOaepEncrypt(data.confirmPwd);
					const submitData = {
						currentPassword,
						plainPassword,
						confirmPlainPassword,
					};
					try {
						if (location.pathname === "/change-pwd-first-login") {
							await firstLoginReset(submitData);
						} else {
							await commonReset(submitData);
						}
					} catch (error) {
						setErrMsg(error);
					} finally {
						setIsLoading(false);
					}
				}}
				onCancel={() => console.log("cancelled.")}
			/>
		));
	};

	const handleLockClose = () => {
		history.push("/login");
	};

	const handleReset = () => reset(defaultValues);

	const handleClickShowCurrentPassword = () => {
		setShowCurrentPassword(!showCurrentPassword);
	};

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	return (
		<div style={{ width: "100%", height: isMobilePhone() ? "auto" : "calc(100vh - 94.5px)" }}>
			<LoadingScreen open={isLoading} />
			{isMobilePhone() ? (
				<div className={classes.mobileTitle}>Change Password</div>
			) : (
				<Typography variant="h4" gutterBottom xs={12} style={{ paddingTop: "0.35em", marginLeft: "15px" }}>
					Change Password
				</Typography>
			)}
			<Grid className={classes.paper} style={{ minWidth: "auto" }}>
				<form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
					<FormControl fullWidth sx={{ mb: "10px" }}>
						<Controller
							render={({ field }) => (
								<TextField
									{...field}
									{...register("currentPwd")}
									label="Current Password"
									type={showCurrentPassword ? "text" : "password"}
									size="small"
									error={errors.currentPwd}
									helperText={errors.currentPwd?.message}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label="toggle password visibility"
													onClick={handleClickShowCurrentPassword}
													onMouseDown={handleMouseDownPassword}
													size="large"
												>
													{showCurrentPassword ? <Visibility /> : <VisibilityOff />}
												</IconButton>
											</InputAdornment>
										),
									}}
									inputProps={{
										maxLength: 48,
										style: { fontFamily: showCurrentPassword || "text-security-disc" },
									}}
								/>
							)}
							control={control}
							name="currentPwd"
						/>
					</FormControl>
					<NewPWInput
						register={register}
						errors={errors}
						classes={classes}
						control={control}
						atLeast8Valid={atLeast8Valid}
						atLeast1Num={atLeast1Num}
						atLeast1LowAndUp={atLeast1LowAndUp}
						atLeast1Special={atLeast1Special}
						notContainEmail={notContainEmail}
					/>
					<div className={classes.input}>
						<Button variant="contained" color="primary" size="small" disabled={!saveEnabled} type="submit">
							Save
						</Button>
						&nbsp;&nbsp;
						<Button
							variant="contained"
							color="primary"
							size="small"
							type="button"
							onClick={() => handleReset()}
						>
							Clear Input
						</Button>
					</div>
				</form>
			</Grid>
			<LockPromptDialog open={showLockTip} onClose={handleLockClose} content={lockTipContent} />
		</div>
	);
}
